import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Repeat, Check, X, Plus, Trash2, FileText, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

/** Compact editor for one historic screening term (session + term + metric values). */
function HistoricTermEditor({ hs, idx, metrics, onChange, onRemove }) {
  const [collapsed, setCollapsed] = useState(false);
  const grouped = useMemo(() => {
    const map = {};
    metrics.forEach((m) => { (map[m.category] = map[m.category] || []).push(m); });
    return Object.entries(map);
  }, [metrics]);

  const setField = (patch) => onChange({ ...hs, ...patch });
  const setValue = (mid, val) => {
    const values = { ...(hs.values || {}) };
    if (val === "" || val === null) delete values[mid];
    else values[mid] = isNaN(Number(val)) ? val : Number(val);
    setField({ values });
  };
  const filledCount = Object.values(hs.values || {}).filter((v) => v !== null && v !== "").length;

  return (
    <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50/30" data-testid={`historic-editor-${idx}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-amber-100/60">
        <div className="flex items-center gap-2 flex-1">
          <FileText className="w-4 h-4 text-amber-700" />
          <span className="font-heading font-bold text-sm text-amber-900">Historic Term #{idx + 1}</span>
          {hs.session && <span className="text-xs font-mono text-amber-700">· {hs.session}</span>}
          {hs.term && <Badge variant="outline" className="text-[10px] uppercase">T{hs.term}</Badge>}
          <Badge variant="outline" className="text-[10px]">{filledCount} value{filledCount === 1 ? "" : "s"}</Badge>
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setCollapsed(!collapsed)} data-testid={`historic-toggle-${idx}`}>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onRemove} data-testid={`historic-remove-${idx}`}>
          <Trash2 className="w-4 h-4 text-rose-500" />
        </Button>
      </div>
      {!collapsed && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Session <span className="text-rose-500">*</span></Label>
              <Input data-testid={`historic-session-${idx}`} value={hs.session} onChange={(e) => setField({ session: e.target.value })} placeholder="2024-25" className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Term <span className="text-rose-500">*</span></Label>
              <select data-testid={`historic-term-${idx}`} value={hs.term} onChange={(e) => setField({ term: Number(e.target.value) })} className="mt-1 h-9 w-full border border-slate-200 rounded-md px-2 text-sm bg-white">
                <option value={1}>Term 1</option>
                <option value={2}>Term 2</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Date</Label>
              <Input data-testid={`historic-date-${idx}`} type="date" value={hs.date} onChange={(e) => setField({ date: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Doctor sign</Label>
              <Input data-testid={`historic-doctor-${idx}`} value={hs.doctor_sign} onChange={(e) => setField({ doctor_sign: e.target.value })} placeholder="Dr Name" className="mt-1 h-9" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Doctor&apos;s remarks / prescription</Label>
            <Textarea data-testid={`historic-remarks-${idx}`} value={hs.remarks} onChange={(e) => setField({ remarks: e.target.value })} placeholder="Overall observations from the previous school…" className="mt-1 font-serif" />
          </div>
          <div className="space-y-3">
            {grouped.map(([cat, mets]) => (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-sky-700 mb-1">{cat}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {mets.map((m) => (
                    <div key={m.id} className="text-xs">
                      <Label className="text-[10px] text-slate-500">{m.name} <span className="text-slate-400">{m.unit && `(${m.unit})`}</span></Label>
                      <Input
                        data-testid={`historic-value-${idx}-${m.id}`}
                        value={hs.values?.[m.id] ?? ""}
                        onChange={(e) => setValue(m.id, e.target.value)}
                        className="mt-1 h-8 font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PortRequestsPanel({ ports, reload }) {
  const [filter, setFilter] = useState("all");
  const [decideOpen, setDecideOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [decision, setDecision] = useState({ status: "approved", admin_notes: "", historic_screenings: [] });
  const [metrics, setMetrics] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics([]));
  }, []);

  const filtered = ports.filter((p) => filter === "all" || p.status === filter);

  const openDecide = (port, status) => {
    setTarget(port);
    setDecision({ status, admin_notes: "", historic_screenings: [] });
    setDecideOpen(true);
  };

  const addHistoricTerm = () => setDecision((d) => ({
    ...d,
    historic_screenings: [...(d.historic_screenings || []), { session: "", term: 1, date: "", doctor_sign: "", remarks: "", values: {} }],
  }));
  const updateHistoric = (i, next) => setDecision((d) => {
    const arr = [...(d.historic_screenings || [])];
    arr[i] = next;
    return { ...d, historic_screenings: arr };
  });
  const removeHistoric = (i) => setDecision((d) => ({
    ...d,
    historic_screenings: (d.historic_screenings || []).filter((_, idx) => idx !== i),
  }));

  const submit = async () => {
    // Validate historic (approve only) — session + term required
    if (decision.status === "approved") {
      const bad = (decision.historic_screenings || []).find((h) => !h.session?.trim() || !h.term);
      if (bad) return toast.error("Each historic term needs a Session (e.g. 2024-25) and a Term.");
    }
    setSaving(true);
    try {
      await http.patch(`/accounts/port/${target.id}`, decision);
      const merged = (decision.historic_screenings || []).length;
      toast.success(
        decision.status === "approved" && merged
          ? `Approved · attached ${merged} historic term${merged > 1 ? "s" : ""}`
          : `Port request ${decision.status}`
      );
      setDecideOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
            <Repeat className="w-4 h-4 text-sky-500" /> Port Requests
          </h2>
          <div className="text-xs text-slate-500">Parents requesting to merge their child&apos;s previous school report.</div>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="port-filter-tabs">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              data-testid={`port-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 h-9 text-xs font-medium capitalize transition-colors ${filter === f ? "bg-sky-500 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {f} {f !== "all" && `(${ports.filter((p) => p.status === f).length})`}
            </button>
          ))}
        </div>
      </div>
      <Card><CardContent className="p-0">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="port-empty-state">
            No {filter === "all" ? "" : filter} port requests.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-ports-table">
              <thead className="text-slate-400 uppercase text-[10px] tracking-widest bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3">Requested</th>
                  <th className="text-left px-4 py-3">Child</th>
                  <th className="text-left px-4 py-3">Parent</th>
                  <th className="text-left px-4 py-3">Previous School</th>
                  <th className="text-left px-4 py-3">Prev KID</th>
                  <th className="text-left px-4 py-3">Notes</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{p.student?.name || "—"}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{p.student?.kid_id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-slate-800">{p.parent?.name || "—"}</div>
                      <div className="text-[11px] text-slate-500">{p.parent?.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{p.previous_school}</td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{p.previous_kid_id || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs max-w-xs truncate" title={p.notes}>{p.notes || "—"}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`uppercase tracking-widest text-[10px] ${STATUS_STYLES[p.status] || ""}`} data-testid={`port-status-${p.id}`}>
                        {p.status}
                      </Badge>
                      {p.admin_notes && (
                        <div className="text-[11px] text-slate-500 mt-1 italic">&ldquo;{p.admin_notes}&rdquo;</div>
                      )}
                      {typeof p.merged_count === "number" && p.merged_count > 0 && (
                        <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {p.merged_count} historic term{p.merged_count > 1 ? "s" : ""} attached
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {p.status === "pending" ? (
                        <>
                          <Button size="sm" variant="ghost" data-testid={`port-approve-${p.id}`} onClick={() => openDecide(p, "approved")} className="text-emerald-600 hover:text-emerald-700">
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`port-reject-${p.id}`} onClick={() => openDecide(p, "rejected")} className="text-rose-600 hover:text-rose-700">
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">reviewed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      <Dialog open={decideOpen} onOpenChange={setDecideOpen}>
        <DialogContent data-testid="port-decide-dialog" className={decision.status === "approved" ? "max-w-4xl max-h-[85vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision.status === "approved" ? <Check className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-rose-500" />}
              {decision.status === "approved" ? "Approve" : "Reject"} port request
            </DialogTitle>
            <DialogDescription>
              {target?.student?.name} · from <span className="font-semibold">{target?.previous_school}</span>
              {target?.previous_kid_id && <> · <span className="font-mono">{target.previous_kid_id}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Admin note <span className="text-slate-400 text-[11px]">(shown to parent)</span></Label>
              <Textarea
                data-testid="port-admin-notes-input"
                value={decision.admin_notes}
                onChange={(e) => setDecision({ ...decision, admin_notes: e.target.value })}
                placeholder={decision.status === "approved" ? "e.g. Records merged for Terms 1-2. Report card available." : "e.g. Unable to verify previous school records. Please contact us with more details."}
                className="mt-1"
              />
            </div>

            {decision.status === "approved" && (
              <div className="pt-2 border-t border-slate-200" data-testid="historic-block">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-sky-700 font-bold">Historic Screening Records <span className="text-slate-400 font-normal">(optional)</span></div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Attach values from the previous school so parents see the full timeline on their Report Card.</div>
                  </div>
                  <Button size="sm" variant="outline" data-testid="add-historic-btn" onClick={addHistoricTerm} className="h-8 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add historic term
                  </Button>
                </div>
                <div className="space-y-3">
                  {(decision.historic_screenings || []).map((hs, i) => (
                    <HistoricTermEditor
                      key={i}
                      hs={hs}
                      idx={i}
                      metrics={metrics}
                      onChange={(next) => updateHistoric(i, next)}
                      onRemove={() => removeHistoric(i)}
                    />
                  ))}
                  {(decision.historic_screenings || []).length === 0 && (
                    <div className="text-[11px] text-slate-400 italic bg-slate-50 rounded-lg p-3 text-center">
                      No historic records yet. Approving without attaching values is fine — you can always re-approve later.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="port-decide-submit-btn"
              onClick={submit}
              disabled={saving}
              className={decision.status === "approved" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"}
            >
              {saving ? "Saving…" : `Confirm ${decision.status === "approved" ? "approval" : "rejection"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
